import type { TimeRangePickerProps } from 'antd';
import { DatePicker } from 'antd';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import { useEffect } from 'react';
import { toDayjs } from '~/utils/datetime';

const { RangePicker } = DatePicker;

const monthPresets: TimeRangePickerProps['presets'] = [
  {
    label: 'This Month',
    value: [dayjs().startOf('month'), dayjs().endOf('month')],
  },
  {
    label: 'Last 3 Months',
    value: [dayjs().subtract(2, 'month').startOf('month'), dayjs().endOf('month')],
  },
  {
    label: 'Last 6 Months',
    value: [dayjs().subtract(5, 'month').startOf('month'), dayjs().endOf('month')],
  },
  {
    label: 'This Year',
    value: [dayjs().startOf('year'), dayjs().endOf('year')],
  },
  {
    label: 'Previous Year',
    value: [dayjs().subtract(1, 'year').startOf('year'), dayjs().subtract(1, 'year').endOf('year')],
  },
];

interface ICustomMonthRangePickerProps {
  onChange(startDate: string, endDate: string): void;
  onClear(): void;
  value: null | Array<string>;
  setDefaultValue?: boolean;
}

const CustomMonthRangePicker = ({ onChange, onClear, value, setDefaultValue }: ICustomMonthRangePickerProps) => {
  useEffect(() => {
    if (setDefaultValue && (value === null || value.length === 0)) {
      // Set 'This Year' value on mount if no value is currently set
      const thisYearPreset = monthPresets.find(preset => preset.label === 'This Year');
      if (thisYearPreset && Array.isArray(thisYearPreset.value)) {
        const [startDate, endDate] = thisYearPreset.value;
        if (startDate && endDate) {
          onChange(startDate.format('YYYY-MM'), endDate.format('YYYY-MM'));
        }
      }
    }
  }, [setDefaultValue, value, onChange]);

  const onRangeChange = (dates: null | (Dayjs | null)[], dateStrings: string[]) => {
    if (dates) {
      onChange(dateStrings[0], dateStrings[1]);
    } else {
      onClear();
    }
  };

  return (
    <RangePicker
      picker="month"
      value={value === null ? value : [toDayjs(value[0]), toDayjs(value[1])]}
      className="customDateInput"
      presets={monthPresets}
      onChange={onRangeChange}
    />
  );
};

export default CustomMonthRangePicker;
